import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Container,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  Snackbar,
  TextField,
  Typography
} from '@mui/material';
import LockResetRoundedIcon from '@mui/icons-material/LockResetRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import apiUrl from '../config';
import '../CSS/Resetpassword.css';

const ResetPassword = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const token = useMemo(() => new URLSearchParams(location.search).get('token'), [location.search]);

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!token) {
      setNotification({ open: true, message: 'Lien invalide ou token manquant.', severity: 'error' });
      return;
    }

    if (newPassword.length < 8) {
      setNotification({ open: true, message: 'Le mot de passe doit contenir au moins 8 caractères.', severity: 'error' });
      return;
    }

    if (newPassword !== confirmPassword) {
      setNotification({ open: true, message: 'Les mots de passe ne correspondent pas.', severity: 'error' });
      return;
    }

    setLoading(true);

    try {
      const response = await axios.put(`${apiUrl}/reset-password?token=${encodeURIComponent(token)}`, {
        new_password: newPassword,
      });

      setNotification({
        open: true,
        message: response.data?.message || 'Mot de passe mis à jour avec succès.',
        severity: 'success',
      });

      setTimeout(() => {
        navigate('/login', { replace: true });
      }, 1800);
    } catch (error) {
      setNotification({
        open: true,
        message: error.response?.data?.message || 'Erreur lors de la réinitialisation du mot de passe.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="reset-page">
      <Container maxWidth="lg" className="reset-shell">
        <Paper elevation={0} className="reset-card">
          <Box className="reset-brand-panel">
            <img src="/NOVA PORTAGE Main Logo.png" alt="Logo Nova Portage" className="reset-logo" />
            <Typography className="reset-eyebrow">Sécurité du compte</Typography>
            <Typography variant="h3" component="h1" className="reset-title">
              Choisissez un nouveau mot de passe.
            </Typography>
            <Typography className="reset-subtitle">
              Votre mot de passe doit rester personnel. Utilisez au moins 8 caractères et évitez les mots de passe déjà utilisés ailleurs.
            </Typography>
          </Box>

          <Box className="reset-form-panel">
            <LockResetRoundedIcon className="reset-main-icon" />
            <Typography variant="h4" component="h2" className="reset-form-title">
              Réinitialisation
            </Typography>
            <Typography className="reset-form-help">
              Renseignez votre nouveau mot de passe pour sécuriser votre accès NOVA.
            </Typography>

            <form onSubmit={handleSubmit} className="reset-form">
              <TextField
                fullWidth
                label="Nouveau mot de passe"
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                autoComplete="new-password"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockRoundedIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword((value) => !value)} edge="end">
                        {showPassword ? <VisibilityOffRoundedIcon /> : <VisibilityRoundedIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="Confirmer le mot de passe"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockRoundedIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowConfirmPassword((value) => !value)} edge="end">
                        {showConfirmPassword ? <VisibilityOffRoundedIcon /> : <VisibilityRoundedIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button type="submit" variant="contained" fullWidth className="reset-submit" disabled={loading}>
                {loading ? 'Mise à jour...' : 'Modifier mon mot de passe'}
              </Button>
            </form>

            <Link component={RouterLink} to="/login" className="reset-login-link">
              Retour à la connexion
            </Link>
          </Box>
        </Paper>
      </Container>

      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={notification.severity} sx={{ width: '100%', fontWeight: 700 }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </main>
  );
};

export default ResetPassword;
